/**
 * ChipSegment — одна ячейка `SegmentedControl`. Своей вёрстки у неё нет:
 * это `ChipMUI` со снятым скруглением, которому группа через контекст
 * подставляет размер, палитру, состояние выбора и обработчик.
 *
 * Внутри `<SegmentedControl mode="radio|checkbox">`:
 *   `<ChipSegment value="phone" leftIcon="call">Телефон</ChipSegment>`
 *   Группа читает `value`, решает, выбрана ли ячейка, и дёргает нужный
 *   колбэк по клику.
 *
 * Внутри `<SegmentedControl mode="button">`:
 *   `<ChipSegment leftIcon="format_bold" onClick={makeBold} />`
 *   Группа даёт только общую обвязку; состояние на совести ячейки.
 *
 * Внутри `<SegmentedControl mode="nav">`:
 *   `<ChipSegment value="pricing" href="#pricing">Тарифы</ChipSegment>`
 *   Ячейка рендерится ссылкой, текущая помечается `aria-current`.
 *
 * Вне группы ведёт себя как обычная пилюля-тогл — со скруглением и
 * собственными `selected` / `onClick`.
 *
 * @format
 */

import {useContext, type MouseEvent, type ReactNode} from 'react';

import {
  ChipMUI,
  type ChipMUIColor,
  type ChipMUIProps,
  type ChipMUISize,
  type ChipMUIVariant,
} from 'ui/components/atoms/ChipMUI';

import {SegmentedControlContext} from './SegmentedControl';

/**
 * Всё, что ячейка решает сама (или получает от группы) — из базового
 * набора пилюли исключено. Остаток — `id`, `title`, `data-*`, прочие
 * `aria-*`, обработчики — проходит на корень как есть.
 */
type ChipPassthrough = Omit<
  ChipMUIProps,
  | 'mode'
  | 'selected'
  | 'color'
  | 'size'
  | 'variant'
  | 'leftIcon'
  | 'children'
  | 'href'
  | 'onClick'
  | 'onRemove'
  | 'disabled'
  | 'fullWidth'
  | 'rounded'
  | 'ariaLabel'
  | 'role'
  | 'aria-checked'
  | 'aria-current'
>;

export interface ChipSegmentProps extends ChipPassthrough {
  /** Идентификатор ячейки для `radio` / `checkbox` / `nav`. В `button` не нужен. */
  value?: string;

  /**
   * Состояние выбора для standalone и `button`-режима. Внутри
   * radio / checkbox / nav игнорируется — там его считает группа.
   */
  selected?: boolean;

  /**
   * Ступень размерной лестницы. Внутри группы размер задаёт она — там
   * этот проп не нужен и разъедет ряд. Он для standalone-ячейки, где
   * группы нет и без него остаётся только дефолтный `m` (48).
   */
  size?: ChipMUISize;

  /**
   * Перебить цвет ЭТОЙ ячейки. Применяется к тому состоянию, в котором
   * ячейка находится: выбранной — вместо группового `color`, незанятой —
   * вместо `idleColor`. Для точечных акцентов вроде «Удалить» в ряду.
   */
  color?: ChipMUIColor;

  /**
   * Перебить вариант ЭТОЙ ячейки. Виден только в незанятом состоянии, и
   * это не guard здесь, а свойство палитры: у выбранной пилюли
   * `paletteFor` возвращает заливку роли раньше, чем доходит до
   * варианта. Так что задавать его вместе с `selected` бессмысленно —
   * не потому, что мы его не передаём, а потому, что его некому читать.
   */
  variant?: ChipMUIVariant;

  /** Лейбл ячейки. */
  children?: ReactNode;

  /** Ведущая иконка — имя Material Symbols или React-элемент. */
  leftIcon?: string | ReactNode;

  /**
   * Адрес перехода. Ячейка рендерится `<a href>` — для якорной
   * навигации (`href='#block'`) это даёт хеш в адресе, «назад» и
   * открытие в новой вкладке. Штатный спутник `mode='nav'`.
   */
  href?: string;

  /**
   * Клик по ячейке. В radio / checkbox / nav вызывается ПОСЛЕ логики
   * группы. Событие приходит наружу — ссылочной ячейке нужен
   * `preventDefault()`, если переход перехватывают плавным скроллом.
   */
  onClick?: (event: MouseEvent<HTMLElement>) => void;

  /** Выключить только эту ячейку. `disabled` всей группы тоже действует. */
  disabled?: boolean;

  /** Доступное имя — обязателен для ячейки без видимого текста. */
  ariaLabel?: string;
}

export function ChipSegment({
  value,
  selected: selectedProp,
  size: sizeProp,
  color: colorProp,
  variant: variantProp,
  children,
  leftIcon,
  href,
  onClick,
  disabled: disabledProp,
  ariaLabel,
  ...rest
}: ChipSegmentProps) {
  const group = useContext(SegmentedControlContext);

  const selected = group
    ? // В radio / checkbox / nav решает группа. В button её вердикт всегда
      // false — отдаём управление собственному пропу ячейки.
      group.mode === 'button'
      ? Boolean(selectedProp)
      : group.resolveSelected(value)
    : Boolean(selectedProp);

  const disabled = Boolean(disabledProp) || Boolean(group?.disabled);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (disabled) return;
    if (group) {
      // Логика группы первой, свой обработчик ячейки — следом, но с тем
      // же событием: иначе из него не сделать preventDefault().
      group.handleClick(value, () => onClick?.(event));
    } else {
      onClick?.(event);
    }
  };

  // Роль ячейки задаёт режим группы. Пилюля сама по себе — тогл-кнопка с
  // aria-pressed; внутри radio / checkbox это виджет выбора с
  // aria-checked; в nav это ссылка, и текущность выражает aria-current
  // (`location` — «этот пункт соответствует текущему месту на странице»).
  let role: string | undefined;
  let ariaChecked: boolean | undefined;
  let ariaCurrent: 'location' | undefined;
  if (group?.mode === 'radio') {
    role = 'radio';
    ariaChecked = selected;
  } else if (group?.mode === 'checkbox') {
    role = 'checkbox';
    ariaChecked = selected;
  } else if (group?.mode === 'nav') {
    ariaCurrent = selected ? 'location' : undefined;
  }

  // Цвет: собственный проп ячейки > групповой (свой для выбранного и
  // незанятого состояния) > дефолт.
  const groupColor = selected ? (group?.color ?? 'primary') : (group?.idleColor ?? 'neutral');

  return (
    <ChipMUI
      {...rest}
      selected={selected}
      color={colorProp ?? groupColor}
      size={sizeProp ?? group?.size ?? 'm'}
      variant={variantProp ?? group?.idleVariant ?? 'solid'}
      leftIcon={leftIcon}
      href={href}
      onClick={handleClick}
      disabled={disabled}
      fullWidth={group?.fullWidth ?? false}
      rounded={!group}
      ariaLabel={ariaLabel}
      role={role}
      aria-checked={ariaChecked}
      aria-current={ariaCurrent}
    >
      {children}
    </ChipMUI>
  );
}
